// ── PipelinePage — funnel + drag-and-drop kanban of open opportunities ──
// Rendered inside the Sales Dashboard, under the Pipeline by Stage card
// (embedded). Props, all optional:
//   embedded   — no page header; the owner filter sits in a slim toolbar
//   stage / onStage — the stage filter, controlled by the dashboard so a click
//                on a Pipeline by Stage row filters this board
//   onChange   — called after a drag changes a stage, so the dashboard's
//                totals follow
// No "+ Add Opportunity" here: opportunities are created from an account or
// by converting a lead, never free-standing.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, Card } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const PipelinePage = ({ embedded = false, stage, onStage, onChange } = {}) => {
  const { api, setModal, role } = useApp();
  const [stages, setStages] = useState([]);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [ownStage, setOwnStage] = useState("");         // set by clicking a funnel chevron
  const stageFilter = stage !== undefined ? stage : ownStage;
  const setStageFilter = onStage || setOwnStage;
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("getOpportunityStages"), api.get("getOpportunities", { open: 1 })])
      .then(([s, o]) => { setStages(Array.isArray(s) ? s : []); setOpps(Array.isArray(o) ? o : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Spinner />; if (err && !opps.length) return <ErrorMsg msg={err} onRetry={load} />;

  const openStages = stages.filter(s => !s.is_closed);          // ordered by sort_order
  const flowStages = openStages.filter(s => s.stage !== "Hold"); // the linear funnel
  const holdStage = openStages.find(s => s.stage === "Hold");

  // Owner filter options (owners that actually have open deals).
  const owners = Array.from(new Map(opps.filter(o => o.owner).map(o => [String(o.owner), o.ownerName || o.owner])).entries())
    .map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const shownOpps = ownerFilter ? opps.filter(o => String(o.owner) === ownerFilter) : opps;

  const byStage = (stage) => shownOpps.filter(o => o.stage === stage);
  const weighted = (x) => parseFloat(x.commitment_amount || 0) * (parseFloat(x.probability != null ? x.probability : x.stage_probability || 0) / 100);
  const colTotal = (arr, fn) => arr.reduce((s, x) => s + fn(x), 0);

  // ── Drag to restage (optimistic; reverts on error) ──
  const move = (id, toStage) => {
    const opp = opps.find(o => String(o.id) === String(id));
    if (!opp || opp.stage === toStage) return;
    const prevStage = opp.stage;
    const t = stages.find(s => s.stage === toStage) || {};
    setOpps(prev => prev.map(o => String(o.id) === String(id)
      ? { ...o, stage: toStage, stage_order: t.sort_order, stage_probability: t.probability, is_won: t.is_won, is_closed: t.is_closed }
      : o));
    api.post("saveOpportunity", { ...opp, stage: toStage })
      .then(() => { if (onChange) onChange(); })
      .catch(e => { setErr(e.message); setOpps(prev => prev.map(o => String(o.id) === String(id) ? { ...o, stage: prevStage } : o)); });
  };
  const canDrag = !!role.canEdit;

  const colHead = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight, display: "flex", justifyContent: "space-between" };

  const chevronOf = (s, i, isHold) => {
    const list = byStage(s.stage);
    const count = list.length;
    const val = colTotal(list, x => parseFloat(x.commitment_amount || 0));
    const wtd = colTotal(list, weighted);
    const fill = isHold ? "#8a8f98" : s.makes_account_visible ? B.amber : B.cyan;
    const textcol = (s.makes_account_visible && !isHold) ? "#4a3800" : "#fff";
    const selected = stageFilter === s.stage;
    const chev = isHold
      ? "none"
      : i === 0
        ? "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"
        : "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)";
    return (
      <div key={s.stage} style={{ flex: (count + 0.5) + " 1 0", minWidth: 96 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.stage}</div>
        <div onClick={() => setStageFilter(stageFilter === s.stage ? "" : s.stage)}
          title={"Filter board to " + s.stage}
          style={{
            background: fill, color: textcol, cursor: "pointer",
            clipPath: chev === "none" ? undefined : chev, WebkitClipPath: chev === "none" ? undefined : chev,
            borderRadius: isHold ? 8 : 0,
            padding: "12px 22px 12px " + (i === 0 && !isHold ? 16 : 24) + "px", minHeight: 58,
            display: "flex", flexDirection: "column", justifyContent: "center",
            outline: selected ? "2px solid #fff" : "none", outlineOffset: -2,
            opacity: (stageFilter && !selected) ? 0.55 : 1, transition: "opacity .15s"
          }}>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{count}</div>
          <div style={{ fontSize: 11, opacity: 0.9, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmt(val)} · wtd {fmt(wtd)}</div>
        </div>
      </div>
    );
  };

  const visibleColumns = stageFilter ? openStages.filter(s => s.stage === stageFilter) : openStages;

  return <>
    {(() => {
      const ownerSel = <select key="owner" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
        style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--color-border-secondary)", fontSize: 13, background: "var(--color-background-primary)", color: "var(--color-text-primary)", cursor: "pointer" }}>
        <option value="">All owners</option>
        {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>;
      return embedded
        ? <div style={{ display: "flex", justifyContent: "flex-end", margin: "16px 0 10px" }}>{ownerSel}</div>
        : <SectionHeader actions={[ownerSel]} />;
    })()}

    {/* ── Funnel ── */}
    {openStages.length > 0 && (
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em" }}>Pipeline</div>
          {stageFilter && <button onClick={() => setStageFilter("")} style={{ fontSize: 11, color: B.cyan, background: "none", border: "none", cursor: "pointer" }}>Filtered to {stageFilter} — show all ✕</button>}
        </div>
        <div style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
          {flowStages.map((s, i) => chevronOf(s, i, false))}
          {holdStage && <>
            <div style={{ width: 1, alignSelf: "stretch", background: "var(--color-border-tertiary)", margin: "18px 8px 0" }} />
            <div style={{ flex: "0 0 120px" }}>{chevronOf(holdStage, 0, true)}</div>
          </>}
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: "var(--color-text-secondary)" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: B.cyan }} /> Sales</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: B.amber }} /> Visible to PMs</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "#8a8f98" }} /> Hold</span>
          {canDrag && <span style={{ marginLeft: "auto", fontStyle: "italic" }}>Drag a card to change its stage</span>}
        </div>
      </Card>
    )}

    {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 8 }}>{err}</div>}

    {/* ── Kanban ── */}
    <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start" }}>
      {visibleColumns.map(col => {
        const list = byStage(col.stage);
        const over = dragOver === col.stage;
        return (
          <div key={col.stage} style={{ minWidth: 240, flex: "0 0 240px" }}
            onDragOver={canDrag ? (e => { e.preventDefault(); if (dragOver !== col.stage) setDragOver(col.stage); }) : undefined}
            onDragLeave={canDrag ? (() => setDragOver("")) : undefined}
            onDrop={canDrag ? (e => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); setDragOver(""); setDragId(null); move(id, col.stage); }) : undefined}>
            <Card style={{ padding: 12, background: "var(--color-background-tertiary)", outline: over ? "2px dashed " + B.cyan : "none", outlineOffset: -2 }}>
              <div style={colHead}>
                <span>{col.stage}</span>
                <span style={{ color: "var(--color-text-secondary)", fontWeight: 600 }}>{list.length}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 10 }}>
                {fmt(colTotal(list, x => parseFloat(x.commitment_amount || 0)))} · wtd {fmt(colTotal(list, weighted))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 30 }}>
                {list.length === 0
                  ? <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "8px 0" }}>—</div>
                  : list.map(o => (
                    <div key={o.id}
                      draggable={canDrag}
                      onDragStart={canDrag ? (e => { e.dataTransfer.setData("text/plain", o.id); e.dataTransfer.effectAllowed = "move"; setDragId(o.id); }) : undefined}
                      onDragEnd={() => { setDragId(null); setDragOver(""); }}
                      onClick={() => setModal({ type: "opp-view", rec: o, accountid: o.accountid, onDone: load })}
                      style={{ background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8, padding: 10, cursor: canDrag ? "grab" : "pointer", opacity: dragId === o.id ? 0.4 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = B.cyan}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--color-border-tertiary)"}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{o.companyname || "—"}</div>
                      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>{o.name || ""}</div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{o.commitment_amount != null ? fmt(o.commitment_amount) : "—"}</span>
                        <span style={{ color: "var(--color-text-secondary)" }}>{o.expected_close_date ? fmtDate(o.expected_close_date) : ""}</span>
                      </div>
                      {o.ownerName && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>{o.ownerName}</div>}
                    </div>
                  ))}
              </div>
            </Card>
          </div>
        );
      })}
    </div>
  </>;
};
